import type { Lead, Question, Answer } from '@/types';
import { APP_CONFIG } from '@/config/constants';
import { ChatContainer } from '../components/chat/ChatContainer';
import { InputField } from '../components/chat/InputField';
import { OptionButtons } from '../components/chat/OptionButtons';
import { ProgressBar } from '../components/progress/ProgressBar';
import { FlowEngine } from '@/services/questionnaire/flow-engine';
import { formatAnswerForDisplay } from '@/services/questionnaire/answer-processor';

export interface ChatScreenOptions {
  lead: Lead;
  questions: Question[];
  onComplete: (answers: Answer[]) => void;
}

export class ChatScreen {
  private container: HTMLElement;
  private options: ChatScreenOptions;
  private flowEngine: FlowEngine;
  private chatContainer: ChatContainer | null = null;
  private progressBar: ProgressBar | null = null;
  private currentInputComponent: InputField | OptionButtons | null = null;
  private companyName: string = '';

  constructor(parentElement: HTMLElement, options: ChatScreenOptions) {
    this.options = options;
    this.flowEngine = new FlowEngine(options.lead.id, options.questions);

    this.container = document.createElement('div');
    this.container.className = 'chat-screen';

    this.render();
    parentElement.appendChild(this.container);

    this.startChat();
  }

  private render(): void {
    this.container.innerHTML = `
      <div class="chat-header">
        <img src="${APP_CONFIG.LOGO_PATH}" alt="${APP_CONFIG.COMPANY_NAME}" class="logo">
        <h1>${APP_CONFIG.DIAGNOSTIC_TITLE}</h1>
      </div>
    `;

    this.progressBar = new ProgressBar(this.container, {
      current: 0,
      total: 0
    });

    const chatArea = document.createElement('div');
    chatArea.id = 'chat-area';
    chatArea.style.flex = '1';
    chatArea.style.display = 'flex';
    chatArea.style.flexDirection = 'column';
    chatArea.style.overflow = 'hidden';
    this.container.appendChild(chatArea);

    this.chatContainer = new ChatContainer(chatArea);
  }

  private async startChat(): Promise<void> {
    await this.delay(500);

    const greeting = `Olá, ${this.options.lead.nome}! Bem-vindo ao diagnóstico da Inova Franquias.`;
    this.chatContainer?.addBotMessage(greeting);

    await this.delay(1000);

    const intro = 'Vou fazer algumas perguntas para avaliar seu perfil. Vamos lá?';
    this.chatContainer?.addBotMessage(intro);

    await this.delay(800);

    this.askNextQuestion();
  }

  private async askNextQuestion(): Promise<void> {
    const question = this.flowEngine.getCurrentQuestion();

    if (!question) {
      this.completeQuestionnaire();
      return;
    }

    const progress = this.flowEngine.getProgress();
    this.progressBar?.update({ current: progress.current + 1, total: progress.total });

    await this.delay(500);
    this.chatContainer?.addTypingIndicator();
    await this.delay(800);
    this.chatContainer?.removeTypingIndicator();

    this.chatContainer?.addBotMessage(question.text);

    await this.delay(300);
    this.showInputForQuestion(question);
  }

  private clearInputArea(): void {
    if (this.currentInputComponent) {
      this.currentInputComponent.remove();
      this.currentInputComponent = null;
    }
    const inputArea = document.getElementById('input-area');
    if (inputArea) inputArea.remove();
  }

  private showInputForQuestion(question: Question): void {
    this.clearInputArea();

    const inputArea = document.createElement('div');
    inputArea.id = 'input-area';
    this.container.appendChild(inputArea);

    // Skip button for optional questions
    if (question.required === false) {
      const skipBtn = document.createElement('button');
      skipBtn.textContent = 'Pular esta pergunta';
      skipBtn.style.cssText = `
        display: block;
        margin: 8px auto 0;
        background: none;
        border: 1px solid #94a3b8;
        color: #64748b;
        padding: 6px 16px;
        border-radius: 6px;
        cursor: pointer;
        font-size: 0.85rem;
      `;
      skipBtn.addEventListener('click', () => {
        this.chatContainer?.addUserMessage('(pergunta pulada)');
        this.clearInputArea();
        this.flowEngine.skipQuestion();
        this.askNextQuestion();
      });

      // We'll append the skip button after the input
      setTimeout(() => {
        const area = document.getElementById('input-area');
        if (area) area.appendChild(skipBtn);
      }, 100);
    }

    switch (question.type) {
      case 'boolean':
        this.currentInputComponent = new OptionButtons(inputArea, {
          options: [
            { value: 'true', label: 'Sim' },
            { value: 'false', label: 'Não' }
          ],
          type: 'boolean',
          onSelect: (value) => this.handleAnswer(question, value as string),
          onRendered: () => this.chatContainer?.scrollToBottom()
        });
        break;

      case 'single':
        if (question.options) {
          this.currentInputComponent = new OptionButtons(inputArea, {
            options: question.options.map(o => ({ value: o.value, label: o.label })),
            type: 'single',
            onSelect: (value) => this.handleAnswer(question, value as string),
            onRendered: () => this.chatContainer?.scrollToBottom()
          });
        }
        break;

      case 'multiple':
        if (question.options) {
          this.currentInputComponent = new OptionButtons(inputArea, {
            options: question.options.map(o => ({ value: o.value, label: o.label })),
            type: 'multiple',
            onSelect: (values) => this.handleAnswer(question, values as string[]),
            onRendered: () => this.chatContainer?.scrollToBottom()
          });
        }
        break;

      case 'currency':
        this.currentInputComponent = new InputField(inputArea, {
          type: 'currency',
          placeholder: '0,00',
          onSubmit: (value) => this.handleAnswer(question, value)
        });
        break;

      case 'number':
        this.currentInputComponent = new InputField(inputArea, {
          type: 'number',
          placeholder: 'Digite um número...',
          onSubmit: (value) => this.handleAnswer(question, value)
        });
        break;

      case 'percentage':
        this.currentInputComponent = new InputField(inputArea, {
          type: 'percentage',
          placeholder: 'Digite a porcentagem...',
          onSubmit: (value) => this.handleAnswer(question, value)
        });
        break;

      case 'text':
      default:
        this.currentInputComponent = new InputField(inputArea, {
          type: 'text',
          placeholder: 'Digite sua resposta...',
          onSubmit: (value) => this.handleAnswer(question, value)
        });
        break;
    }

    // Scroll after all input types so the last question stays visível,
    // then focus. Delay allows the browser to finish layout before scrolling.
    if (this.currentInputComponent instanceof InputField) {
      setTimeout(() => {
        this.chatContainer?.scrollToBottomImmediate();
        (this.currentInputComponent as InputField)?.focus();
      }, 150);
    }
  }

  private async handleAnswer(question: Question, rawValue: string | string[]): Promise<void> {
    this.flowEngine.submitAnswer(rawValue as string | string[] | boolean);

    const answer = this.flowEngine.getAnswerByQuestionId(question.id);

    // URL validation for website
    if (question.id === 'cred_website_url') {
      const urlInput = (rawValue as string).trim();
      const urlToTest = urlInput.startsWith('http') ? urlInput : `https://${urlInput}`;
      let urlValid = false;
      try {
        const parsed = new URL(urlToTest);
        urlValid = parsed.hostname.includes('.');
      } catch {
        urlValid = false;
      }
      if (!urlValid) {
        this.flowEngine.removeLastAnswer();
        this.clearInputArea();
        await this.delay(400);
        this.chatContainer?.addBotMessage('URL inválida. Por favor, informe um endereço válido (ex: www.suamarca.com.br):');
        await this.delay(300);
        this.showInputForQuestion(question);
        return;
      }
    }

    // Instagram handle validation
    if (question.id === 'cred_instagram_handle') {
      const raw = (rawValue as string).trim();
      const handle = raw.startsWith('@') ? raw : `@${raw}`;
      const instagramRegex = /^@[a-zA-Z0-9._]{1,30}$/;
      if (!instagramRegex.test(handle)) {
        this.flowEngine.removeLastAnswer();
        this.clearInputArea();
        await this.delay(400);
        this.chatContainer?.addBotMessage('Instagram inválido. Informe no formato @nomedaconta (sem espaços ou caracteres especiais):');
        await this.delay(300);
        this.showInputForQuestion(question);
        return;
      }
      // Normalize: update the stored answer to always include @
      const storedAnswer = this.flowEngine.getAnswerByQuestionId(question.id);
      if (storedAnswer && !String(storedAnswer.value).startsWith('@')) {
        storedAnswer.value = handle;
      }
    }

    // Faturamento correction: navigate back to re-ask fin_gross_revenue
    if (question.id === 'fin_low_revenue_confirm' && rawValue === 'erro') {
      if (answer) {
        this.chatContainer?.addUserMessage(formatAnswerForDisplay(question, answer));
      }
      this.flowEngine.navigateBackToQuestion('fin_gross_revenue');
      this.clearInputArea();
      await this.delay(500);
      this.chatContainer?.addTypingIndicator();
      await this.delay(800);
      this.chatContainer?.removeTypingIndicator();
      this.chatContainer?.addBotMessage('Tudo bem! Por favor, informe novamente o faturamento bruto médio mensal:');
      await this.delay(300);
      const currentQ = this.flowEngine.getCurrentQuestion();
      if (currentQ) this.showInputForQuestion(currentQ);
      return;
    }

    // Cross-question validation (e.g., faturamento líquido < bruto)
    if (answer && question.validationRule?.type === 'must_be_less_than_question') {
      const refAnswer = this.flowEngine.getAnswerByQuestionId(question.validationRule.referenceQuestionId);
      if (refAnswer &&
          typeof answer.value === 'number' &&
          typeof refAnswer.value === 'number' &&
          answer.value >= refAnswer.value) {
        // Remove the invalid answer and re-ask
        this.flowEngine.removeLastAnswer();
        this.clearInputArea();

        await this.delay(400);
        this.chatContainer?.addBotMessage(question.validationRule.errorMessage);
        await this.delay(600);
        this.showInputForQuestion(question);
        return;
      }
    }

    if (answer) {
      const displayValue = formatAnswerForDisplay(question, answer);
      this.chatContainer?.addUserMessage(displayValue);

      if (question.id === 'company_name' && typeof answer.value === 'string') {
        this.companyName = answer.value;
        await this.delay(500);
        this.chatContainer?.addBotMessage(`Ótimo! Vamos conhecer melhor a ${this.companyName}.`);
      }
    }

    this.clearInputArea();
    this.askNextQuestion();
  }

  private async completeQuestionnaire(): Promise<void> {
    this.progressBar?.complete();

    await this.delay(500);
    this.chatContainer?.addTypingIndicator();
    await this.delay(1000);
    this.chatContainer?.removeTypingIndicator();

    const companyText = this.companyName ? ` da ${this.companyName}` : '';
    this.chatContainer?.addBotMessage(`Excelente! Concluímos o diagnóstico${companyText}. Aguarde enquanto processamos suas respostas...`);

    const state = this.flowEngine.complete();
    this.options.onComplete(state.answers);
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  remove(): void {
    this.container.remove();
  }

  getElement(): HTMLElement {
    return this.container;
  }
}
